import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { Box, Button, Dialog, DialogContent, Stack, styled, Typography } from '@mui/material';
import { ComponentProps, FC, useMemo, useState } from 'react';
import { useReleases } from 'src/hooks/notifications/releases/useReleases';
import { NotificationSourceTypeEnum } from 'src/infrastructure/rest-api/api-types';
import { DialogTitleWithCloseIcon } from '../DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';

export const VersionLog: FC<Omit<ComponentProps<'div'>, 'ref'>> = () => {
    const { data } = useReleases();
    const [modalOpen, setModalOpen] = useState(false);

    const latestRelease = useMemo(() => data?.[0], [data]);

    const handleShowDetails = () => {
        setModalOpen(true);
    };

    if (data && latestRelease)
        return (
            <>
                <Button
                    startIcon={<NewReleasesIcon fontSize="small" />}
                    variant="text"
                    onClick={handleShowDetails}
                    sx={{ color: 'white' }}
                >
                    Releases
                </Button>
                <Dialog
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    aria-labelledby="release-dialog-title"
                    aria-describedby="release-dialog-description"
                >
                    <DialogTitleWithCloseIcon title="Latest releases" handleClose={() => setModalOpen(false)} />
                    <DialogContent>
                        {data.slice(0, 5).map((release) => {
                            return (
                                <Stack
                                    direction="column"
                                    key={release.dateTime + release.title}
                                    sx={{
                                        mb: 4,
                                        gap: 2,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        sx={{
                                            alignItems: 'center',
                                            gap: 2,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle1">Release</Typography>
                                        <Typography variant="subtitle2">{release.dateTime}</Typography>
                                    </Stack>
                                    {release.body.map((body) => (
                                        <Stack
                                            direction="row"
                                            key={body.text}
                                            sx={{
                                                alignItems: 'center',
                                                gap: 5,
                                            }}
                                        >
                                            <VersionTag type={body.type}></VersionTag>
                                            <Typography variant="body2">{body.text}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            );
                        })}
                    </DialogContent>
                </Dialog>
            </>
        );
    return null;
};

interface VersionTagProps {
    type: NotificationSourceTypeEnum;
}

const VersionTag: FC<VersionTagProps> = ({ type }) => {
    const { color, tag } = useMemo(() => {
        switch (type) {
            case 'GITHUB_STOREFORECAST_FRONTEND':
                return { color: '#8884d8', tag: 'FE' };
            case 'GITHUB_STOREFORECAST_API':
                return { color: '#82ca9d', tag: 'API' };
            case 'GITHUB_STOREFORECAST_POWERBI':
                return { color: '#f2c811', tag: 'PBI' };
            default:
                return { color: '#c11513', tag: '??' };
        }
    }, [type]);
    return <Tag color={color}>{tag}</Tag>;
};

const Tag = styled(Box)<{ color: string }>(({ color, theme }) => ({
    width: 35,
    height: 20,
    backgroundColor: color,
    color: theme.palette.common.white,
    position: 'relative',
    textAlign: 'center',
    borderTopLeftRadius: 2,
    borderBottomLeftRadius: 2,
    fontWeight: 500,
    '&:after': {
        content: "''",
        position: 'absolute',
        top: 0,
        right: '-14.5px',
        width: 0,
        height: 0,
        borderTop: '10px solid transparent',
        borderBottom: '10px solid transparent',
        borderLeft: `15px solid ${color}`,
    },
}));
